import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface Notice {
  id: number;
  type: string;
  typeBg: string;
  typeText: string;
  text: string;
  href: string;
}

const notices: Notice[] = [
  {
    id: 1,
    type: 'NEW',
    typeBg: 'bg-white',
    typeText: 'text-black',
    text: '4月新品50款已到货！立即查看食品·化妆品新品 / 4월 신상품 50종 입고 완료',
    href: '/#recommended',
  },
  {
    id: 2,
    type: 'EVENT',
    typeBg: 'bg-red-500',
    typeText: 'text-white',
    text: '4月特别活动 — 100箱以上订购享免费配送+额外3%折扣！/ 100박스 이상 무료배송+3% 추가할인',
    href: '/#inquiry',
  },
  {
    id: 3,
    type: 'PROMO',
    typeBg: 'bg-amber-500',
    typeText: 'text-white',
    text: '团购专属特价通知 — 批发买家专属报价，立即询价 / 공동구매 전용 특가, 도매 바이어 단가 문의 접수 중',
    href: '/#inquiry',
  },
  {
    id: 4,
    type: 'INFO',
    typeBg: 'bg-gray-500',
    typeText: 'text-white',
    text: '5月定期配送截止：4/25(周四)下午3时前下单，5月1日确认出货 / 5월 정기배송 마감 4/25',
    href: '',
  },
];

const DISMISS_KEY = 'b2b_banner_dismissed_at';
const BANNER_HEIGHT = 44;

export default function NoticeBanner() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - Number(dismissedAt);
      if (elapsed < 24 * 60 * 60 * 1000) {
        document.documentElement.style.setProperty('--banner-height', '0px');
        return;
      }
    }
    setVisible(true);
    document.documentElement.style.setProperty('--banner-height', `${BANNER_HEIGHT}px`);
    return () => {
      document.documentElement.style.setProperty('--banner-height', '0px');
    };
  }, []);

  const goTo = useCallback((idx: number) => {
    if (fading) return;
    if (timerRef.current) clearInterval(timerRef.current);
    setFading(true);
    setTimeout(() => {
      setCurrent(idx);
      setFading(false);
      // 수동 전환 후 타이머 재시작
      timerRef.current = setInterval(() => {
        setCurrent(prev => (prev + 1) % notices.length);
      }, 5000);
    }, 200);
  }, [fading]);

  const goNext = useCallback(() => {
    goTo((current + 1) % notices.length);
  }, [current, goTo]);

  const goPrev = useCallback(() => {
    goTo((current - 1 + notices.length) % notices.length);
  }, [current, goTo]);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % notices.length);
    }, 5000);
  }, []);

  useEffect(() => {
    if (!visible) return;
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [visible, startTimer]);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    document.documentElement.style.setProperty('--banner-height', '0px');
  };

  const handleNoticeClick = () => {
    const { href } = notices[current];
    if (!href) return;
    if (href.startsWith('/#')) {
      const id = href.replace('/#', '');
      if (window.location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        }, 400);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
    }
  };

  if (!visible) return null;

  const notice = notices[current];

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[55] bg-black text-white flex items-center select-none"
      style={{ height: BANNER_HEIGHT }}
    >
      {/* Left spacer for balance */}
      <div className="w-16 shrink-0 hidden sm:block" />

      {/* Center content */}
      <div className="flex-1 flex items-center justify-center gap-2.5 px-3 min-w-0">
        <span
          className={`shrink-0 text-[9px] font-black px-2 py-0.5 rounded-sm tracking-wider ${notice.typeBg} ${notice.typeText}`}
        >
          {notice.type}
        </span>

        <button
          onClick={handleNoticeClick}
          className={`text-xs font-medium text-white/85 truncate transition-opacity duration-200 whitespace-nowrap ${fading ? 'opacity-0' : 'opacity-100'} ${notice.href ? 'hover:text-white hover:underline cursor-pointer' : 'cursor-default'}`}
        >
          {notice.text}
        </button>

        {/* Dot navigation */}
        <div className="hidden md:flex items-center gap-1 shrink-0 ml-1">
          {notices.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`rounded-full transition-all duration-300 cursor-pointer ${
                i === current ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/30 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Right controls */}
      <div className="flex items-center gap-0.5 pr-3 shrink-0">
        <button
          onClick={goPrev}
          className="w-7 h-7 flex items-center justify-center text-white/50 hover:text-white transition-colors cursor-pointer rounded"
          aria-label="이전 공지"
        >
          <i className="ri-arrow-left-s-line text-base"></i>
        </button>
        <button
          onClick={goNext}
          className="w-7 h-7 flex items-center justify-center text-white/50 hover:text-white transition-colors cursor-pointer rounded"
          aria-label="다음 공지"
        >
          <i className="ri-arrow-right-s-line text-base"></i>
        </button>
        <div className="w-px h-4 bg-white/20 mx-1" />
        <button
          onClick={dismiss}
          className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer rounded"
          aria-label="공지 닫기"
        >
          <i className="ri-close-line text-base"></i>
        </button>
      </div>
    </div>
  );
}
